import { useEffect, useRef, useState, useCallback } from 'react';
import { Sparkles, ChevronDown, X, ChevronRight, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSession } from '../../context/SessionContext';
import { apiClient } from '../../api/client';
import './PersonaSelector.css';

export function PersonaSelector() {
  const { t } = useTranslation();
  const { selectedPersona, setSelectedPersona } = useSession();
  const [open, setOpen] = useState(false);
  const [circles, setCircles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hint, setHint] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [expandedCircle, setExpandedCircle] = useState(null);
  const wrapperRef = useRef(null);

  const fetchCircles = useCallback(async (force = false) => {
    setLoading(true);
    setError(null);
    setHint(null);
    try {
      const { circles: list, hint: apiHint } = await apiClient.listPersonas({ force });
      const arr = Array.isArray(list) ? list : [];
      setCircles(arr);
      if (apiHint) setHint(apiHint);
      if (arr.length === 1) setExpandedCircle(arr[0].id);
      setHasFetched(true);
    } catch (err) {
      setError(err.message || 'Failed to load personas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    if (hasFetched) return;
    fetchCircles(false);
  }, [open, hasFetched, fetchCircles]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const pickCircleLead = (circle) => {
    setSelectedPersona({
      circleId: circle.id,
      circleName: circle.name,
      personaId: null,
      name: circle.name,
    });
    setOpen(false);
  };

  const pickPersona = (circle, persona) => {
    setSelectedPersona({
      circleId: circle.id,
      circleName: circle.name,
      personaId: persona.id,
      name: persona.name,
    });
    setOpen(false);
  };

  const clear = (e) => {
    e.stopPropagation();
    setSelectedPersona(null);
  };

  const labelText = selectedPersona
    ? selectedPersona.name
    : t('persona.none', 'Persona');

  return (
    <div className="persona-selector" ref={wrapperRef}>
      <div className={`persona-trigger ${selectedPersona ? 'active' : ''}`}>
        <button
          type="button"
          className="persona-trigger-main"
          onClick={() => setOpen(!open)}
          title={selectedPersona
            ? `${t('persona.active', 'Aktiv')}: ${selectedPersona.circleName} / ${selectedPersona.name}`
            : t('persona.selectTitle', 'Persona auswählen')}
        >
          <Sparkles size={14} />
          <span className="persona-label">{labelText}</span>
          {!selectedPersona && <ChevronDown size={12} />}
        </button>
        {selectedPersona && (
          <button
            type="button"
            className="persona-clear"
            onClick={clear}
            aria-label={t('persona.clear', 'Persona entfernen')}
            title={t('persona.clear', 'Persona entfernen')}
          >
            <X size={12} />
          </button>
        )}
      </div>

      {open && (
        <div className="persona-dropdown">
          <div className="persona-dropdown-header">
            <span className="persona-dropdown-title">{t('persona.dropdownTitle', 'Persona-Kreise')}</span>
            <button
              type="button"
              className="persona-refresh-btn"
              onClick={() => fetchCircles(true)}
              disabled={loading}
              title={t('persona.refresh', 'Liste aktualisieren')}
            >
              <RefreshCw size={12} className={loading ? 'spinning' : ''} />
            </button>
          </div>
          {loading && <div className="persona-state">{t('persona.loading', 'Lade Personas…')}</div>}
          {error && <div className="persona-state persona-error">{error}</div>}
          {!loading && !error && circles.length === 0 && (
            <div className="persona-state">
              <strong>{t('persona.empty', 'Keine Persona-Kreise verfügbar.')}</strong>
              <div style={{ marginTop: '0.4rem', fontSize: '0.75rem', color: 'var(--text-tertiary)', lineHeight: 1.4 }}>
                {hint || t('persona.emptyHint', 'Im nyxCore-Dashboard unter Persona Studio Kreise erstellen und auf "Published" setzen.')}
              </div>
            </div>
          )}
          {!loading && !error && circles.map((circle) => {
            const isExpanded = expandedCircle === circle.id;
            return (
              <div key={circle.id} className="persona-circle-block">
                <button
                  type="button"
                  className="persona-circle-header"
                  onClick={() => setExpandedCircle(isExpanded ? null : circle.id)}
                >
                  <ChevronRight
                    size={12}
                    className={`persona-chevron ${isExpanded ? 'open' : ''}`}
                  />
                  <div className="persona-circle-meta">
                    <div className="persona-circle-name">{circle.name}</div>
                    {circle.tagline && <div className="persona-circle-tagline">{circle.tagline}</div>}
                  </div>
                  <span className="persona-circle-count">{circle.personas?.length || 0}</span>
                </button>

                {isExpanded && (
                  <div className="persona-circle-personas">
                    <button
                      type="button"
                      className="persona-option persona-lead-option"
                      onClick={() => pickCircleLead(circle)}
                    >
                      <div className="persona-option-name">
                        {t('persona.useLead', 'Lead-Persona des Kreises')}
                      </div>
                      <div className="persona-option-desc">
                        {t('persona.leadHint', 'nyxCore wählt automatisch die führende Persona')}
                      </div>
                    </button>
                    {(circle.personas || []).map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        className={`persona-option ${selectedPersona?.personaId === p.id ? 'selected' : ''}`}
                        onClick={() => pickPersona(circle, p)}
                      >
                        <div className="persona-option-row">
                          {p.imageUrl && (
                            <img src={p.imageUrl} alt="" className="persona-avatar" />
                          )}
                          <div className="persona-option-text">
                            <div className="persona-option-name">
                              {p.name}
                              {p.isLead && <span className="persona-lead-badge">Lead</span>}
                            </div>
                            {p.profile?.role && (
                              <div className="persona-option-desc">{p.profile.role}</div>
                            )}
                            {!p.profile?.role && p.description && (
                              <div className="persona-option-desc">{p.description}</div>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default PersonaSelector;
